import { css } from "@emotion/css";
import { createContext } from "react";
import { CheckboxGroupProps as ReactAriaCheckboxGroupProps } from "react-aria-components";

import { _CheckboxGroup } from "./assets/_CheckboxGroup";
import { _Text } from "../../../_assets/_Text";
import {
  FieldErrors,
  FieldErrorsProps,
} from "../../_assets/input-wrapper/FieldErrors";
import { FormLabelRAC } from "../../_assets/input-wrapper/FormLabelRAC";

export interface CheckboxGroupProps
  extends Omit<ReactAriaCheckboxGroupProps, "children"> {
  children?: React.ReactNode;
  label?: string;
  info?: React.ReactNode;
  description?: string;
  errors?: FieldErrorsProps["children"];
  isCard?: boolean | undefined;
  orientation?: "horizontal" | "vertical";
  stretchChildren?: boolean;
  testID?: string;
}

interface CheckboxGroupContextType {
  isCard?: boolean;
  isGroup: boolean;
}

export const CheckboxGroupContext = createContext<CheckboxGroupContextType>({
  isCard: false,
  isGroup: false,
});

export function CheckboxGroup(props: CheckboxGroupProps) {
  const {
    description,
    children,
    orientation,
    stretchChildren = false,
    testID,
    ...checkboxGroupProps
  } = props;

  return (
    <CheckboxGroupContext.Provider
      value={{ isCard: props.isCard, isGroup: true }}
    >
      <_CheckboxGroup {...checkboxGroupProps} data-testid={testID}>
        {props.label && (
          <FormLabelRAC isRequired={props.isRequired} info={props.info}>
            {props.label}
          </FormLabelRAC>
        )}
        {orientation === "horizontal" ? (
          <div
            className={css(
              styles.horizontal,
              stretchChildren && styles.stretchChildren
            )}
          >
            {children}
          </div>
        ) : (
          children
        )}
        {description && (
          <_Text slot="description" style="input/helper" color="text-weak">
            {description}
          </_Text>
        )}
        <FieldErrors>{props.errors}</FieldErrors>
      </_CheckboxGroup>
    </CheckboxGroupContext.Provider>
  );
}

const styles = {
  horizontal: css({
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    alignItems: "center",
    gap: "var(--space-6)",
    width: "fit-content",
  }),
  stretchChildren: css({
    width: "100%",
    gap: "var(--space-2)",
    "& > *": {
      flex: 1,
    },
  }),
};
